import { useEffect, useState } from 'react';
import { api } from '../api.js';

export default function DqaGuardian({ onGoToKpi }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [filterDim, setFilterDim] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');

  async function runAudit() {
    setLoading(true);
    setErr('');
    try {
      const res = await api.getDqaAudit();
      setData(res);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    runAudit();
  }, []);

  const getStatusBadge = (status, severity) => {
    if (status === 'PASS') {
      return <span className="cbadge proc" style={{ background: '#059669', color: '#fff' }}>✓ PASS</span>;
    }
    if (severity === 'Critical') {
      return <span className="cbadge" style={{ background: '#7f1d1d', color: '#fecaca', fontWeight: 700 }}>🚨 CRITICAL BREACH</span>;
    }
    return <span className="cbadge" style={{ background: '#dc2626', color: '#fff' }}>✕ FAIL ({severity})</span>;
  };

  if (loading && !data) {
    return <div className="card"><p className="muted">Running WHO DQA automated audit on clinical database…</p></div>;
  }

  if (err && !data) {
    return (
      <div className="card">
        <h2>WHO Data Quality Guardian</h2>
        <p className="err">{err}</p>
        <button className="pri sm" onClick={runAudit}>Retry Audit</button>
      </div>
    );
  }

  const checks = data?.checks || [];
  const dimensions = ['All', ...new Set(checks.map((c) => c.dimension))];

  let filtered = checks;
  if (filterDim !== 'All') filtered = filtered.filter((c) => c.dimension === filterDim);
  if (filterStatus === 'PASS') filtered = filtered.filter((c) => c.status === 'PASS');
  if (filterStatus === 'FAIL') filtered = filtered.filter((c) => c.status === 'FAIL');

  const passedCount = data?.passedCount || 0;
  const totalCount = data?.totalRules || 24;
  const score = data?.complianceScore || 100;
  const criticalBreaches = checks.filter((c) => c.status === 'FAIL' && c.severity === 'Critical');

  return (
    <div className="card" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>🛡️</span> WHO Data Quality Assessment (DQA) Guardian
          </h2>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Automated Auditing across 10 WHO Dimensions &amp; 24 Field-Level Integrity Rules
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onGoToKpi && (
            <button className="ghost sm" onClick={onGoToKpi}>
              📊 Executive KPIs →
            </button>
          )}
          <button className="pri sm" onClick={runAudit} disabled={loading}>
            {loading ? 'Running Audit…' : '⚡ Run Live Audit'}
          </button>
        </div>
      </div>

      {/* Audit Score Banner */}
      <div style={{
        background: score >= 95 ? 'rgba(5, 150, 105, 0.12)' : 'rgba(220, 38, 38, 0.12)',
        border: score >= 95 ? '1px solid rgba(5, 150, 105, 0.3)' : '1px solid rgba(239, 68, 68, 0.3)',
        borderRadius: 8,
        padding: '16px 20px',
        marginBottom: 20,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 16
      }}>
        <div>
          <div style={{ color: score >= 95 ? '#34d399' : '#f87171', fontSize: '0.85rem', fontWeight: 700, textTransform: 'uppercase' }}>
            Overall Data Quality Index
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: '2.4rem', fontWeight: 800, color: score >= 95 ? '#10b981' : '#ef4444' }}>
              {score}%
            </span>
            <span style={{ color: '#94a3b8', fontSize: '0.95rem' }}>
              ({passedCount} / {totalCount} rules passed)
            </span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#64748b', marginTop: 4 }}>
            Last Audited: {data?.auditTimestamp ? new Date(data.auditTimestamp).toLocaleString() : 'Just now'}
          </div>
        </div>

        {criticalBreaches.length > 0 ? (
          <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 16px', borderRadius: 6, fontWeight: 700, fontSize: '0.9rem' }}>
            🚨 {criticalBreaches.length} Critical Quality Violation(s) Found!
          </div>
        ) : (
          <div style={{ background: '#064e3b', color: '#a7f3d0', padding: '10px 16px', borderRadius: 6, fontWeight: 600, fontSize: '0.9rem' }}>
            ✓ 100% Zero-Tolerance Compliance Maintained
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div className="tabs" style={{ margin: 0 }}>
          {['All', 'PASS', 'FAIL'].map((s) => (
            <button
              key={s}
              className={'tab' + (filterStatus === s ? ' on' : '')}
              onClick={() => setFilterStatus(s)}
            >
              {s === 'All' ? 'All Checks' : s === 'PASS' ? 'Passed Checks' : 'Failed Checks'}
            </button>
          ))}
        </div>

        <div>
          <select
            value={filterDim}
            onChange={(e) => setFilterDim(e.target.value)}
            style={{ padding: '6px 12px', borderRadius: 6, background: '#0a1929', color: '#fff', border: '1px solid rgba(56, 189, 248, 0.3)' }}
          >
            {dimensions.map((d) => (
              <option key={d} value={d}>
                Dimension: {d}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Rules Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.88rem' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(56, 189, 248, 0.2)', color: '#38bdf8' }}>
              <th style={{ padding: '10px 12px' }}>Dimension</th>
              <th style={{ padding: '10px 12px' }}>Audit Rule &amp; Description</th>
              <th style={{ padding: '10px 12px' }}>Status</th>
              <th style={{ padding: '10px 12px' }}>Evaluation Details</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  background: c.status === 'FAIL' ? 'rgba(239, 68, 68, 0.05)' : 'transparent'
                }}
              >
                <td style={{ padding: '10px 12px', fontWeight: 600, color: '#94a3b8' }}>
                  {c.dimension}
                </td>
                <td style={{ padding: '10px 12px', color: '#f8fafc', fontWeight: 600 }}>
                  {c.rule}
                </td>
                <td style={{ padding: '10px 12px' }}>
                  {getStatusBadge(c.status, c.severity)}
                </td>
                <td style={{ padding: '10px 12px', color: c.status === 'FAIL' ? '#fca5a5' : '#cbd5e1' }}>
                  {c.details}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* WHO DQA Guidelines Footer */}
      <div style={{ marginTop: 24, padding: 14, background: '#020b14', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)', fontSize: '0.82rem', color: '#64748b' }}>
        <strong>WHO Data Quality Dimension Reference:</strong>
        <p style={{ margin: '6px 0 0', lineHeight: 1.6 }}>
          1. Uniqueness (Duplicate NRIC) · 2. Validity (DOB, Dates) · 3. Completeness (Mandatory Allergies) · 4. Workforce Governance (Nurse APC validity) · 5. Clinical Safety (Unaddressed MEWS ≥4) · 6. Medication Safety (5 Rights adherence) · 7. Care Plan Alignment (Weekly reviews).
        </p>
      </div>
    </div>
  );
}
